import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { Eye, Edit, Trash2, Plus } from "lucide-react";
import {
    getRecoveryTasksByBCP,
    deleteRecoveryTask,
    updateRecoveryTaskProgress,
    updateRecoveryTaskStatus,
    getRecoveryStatistics,
} from "../../../service/api";

interface RecoveryTask {
    _id: string;
    bcpPlanId: string;
    taskName: string;
    description: string;
    priority: "Critical" | "High" | "Medium" | "Low";
    status: "Pending" | "In Progress" | "Completed" | "Blocked";
    assignedUserId?: string;
    assignedUserName?: string;
    startDate?: string;
    targetDate?: string;
    estimatedDuration?: number;
    dependencies?: string[];
    progress: number;
    createdAt?: string;
    updatedAt?: string;
}

interface TaskStatistics {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    pendingTasks: number;
    averageProgress: number;
}

const RecoveryTasks: React.FC = () => {
    const navigate = useNavigate();
    const { planId } = useParams<{ planId: string }>();
    const [rowData, setRowData] = useState<RecoveryTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("All");
    const [statistics, setStatistics] = useState<TaskStatistics | null>(null);
    const gridRef = useRef<AgGridReact>(null);

    useEffect(() => {
        if (planId) {
            fetchTasks();
            fetchStatistics();
        }
    }, [planId, statusFilter]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const response = await getRecoveryTasksByBCP(planId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                let tasks = Array.isArray(response.data.data)
                    ? response.data.data
                    : [];

                // Filter by status if not "All"
                if (statusFilter !== "All") {
                    tasks = tasks.filter((task: RecoveryTask) => task.status === statusFilter);
                }

                setRowData(tasks);
            }
        } catch (error: any) {
            console.error("Error fetching recovery tasks:", error);
            toast.error(
                error.response?.data?.resMessage || "Failed to fetch recovery tasks"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const response = await getRecoveryStatistics(planId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this recovery task?")) {
            try {
                const response = await deleteRecoveryTask(id);
                if (response.data?.resCode === 200) {
                    toast.success("Recovery task deleted successfully");
                    fetchTasks();
                    fetchStatistics();
                }
            } catch (error: any) {
                console.error("Error deleting recovery task:", error);
                toast.error(
                    error.response?.data?.resMessage || "Failed to delete recovery task"
                );
            }
        }
    };

    const handleUpdateProgress = async (id: string, currentProgress: number) => {
        const newProgress = prompt(
            `Update progress (0-100). Current: ${currentProgress}%`,
            currentProgress.toString()
        );
        if (newProgress !== null) {
            const progress = parseInt(newProgress);
            if (progress >= 0 && progress <= 100) {
                try {
                    const response = await updateRecoveryTaskProgress(id, progress);
                    if (response.data?.resCode === 200) {
                        toast.success("Progress updated successfully");
                        fetchTasks();
                        fetchStatistics();
                    }
                } catch (error: any) {
                    console.error("Error updating progress:", error);
                    toast.error(
                        error.response?.data?.resMessage || "Failed to update progress"
                    );
                }
            } else {
                toast.error("Progress must be between 0 and 100");
            }
        }
    };

    const getPriorityBadge = (priority: string) => {
        const colors: Record<string, string> = {
            Critical: "bg-red-100 text-red-800",
            High: "bg-orange-100 text-orange-800",
            Medium: "bg-yellow-100 text-yellow-800",
            Low: "bg-green-100 text-green-800",
        };
        return (
            <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[priority] || "bg-gray-100 text-gray-800"
                    }`}
            >
                {priority}
            </span>
        );
    };

    const getStatusBadge = (status: string) => {
        const colors: Record<string, string> = {
            Pending: "bg-gray-100 text-gray-800",
            "In Progress": "bg-blue-100 text-blue-800",
            Completed: "bg-green-100 text-green-800",
            Blocked: "bg-red-100 text-red-800",
        };
        return (
            <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || "bg-gray-100 text-gray-800"
                    }`}
            >
                {status}
            </span>
        );
    };

    const columnDefs = [
        {
            field: "taskName",
            headerName: "Task Name",
            minWidth: 200,
            flex: 1.5,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
        },
        {
            field: "priority",
            headerName: "Priority",
            minWidth: 120,
            flex: 0.8,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            cellRenderer: (params: any) => getPriorityBadge(params.value),
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 130,
            flex: 0.9,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            cellRenderer: (params: any) => getStatusBadge(params.value),
        },
        {
            field: "progress",
            headerName: "Progress",
            minWidth: 150,
            flex: 1,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            cellRenderer: (params: any) => {
                const progress = params.value || 0;
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-teal-500 h-2 rounded-full"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <span className="text-xs font-semibold">{progress}%</span>
                    </div>
                );
            },
        },
        {
            field: "assignedUserName",
            headerName: "Assigned To",
            minWidth: 150,
            flex: 1,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            valueGetter: (params: any) => params.data?.assignedUserName || "Unassigned",
        },
        {
            field: "targetDate",
            headerName: "Target Date",
            minWidth: 130,
            flex: 0.9,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            valueFormatter: (params: any) => {
                if (!params.value) return "N/A";
                return new Date(params.value).toLocaleDateString();
            },
        },
        {
            headerName: "Actions",
            flex: 1,
            headerClass: "bg-[#e9f5ff]",
            cellStyle: { padding: "10px 10px 0 10px" },
            cellRenderer: (params: any) => {
                return (
                    <div className="flex gap-2">
                        <button
                            onClick={() =>
                                navigate(
                                    `/external-emergency-preparedness/bcp-plans/${planId}/recovery-tasks/${params.data._id}?mode=view`
                                )
                            }
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="View"
                        >
                            <Eye size={16} />
                        </button>
                        <button
                            onClick={() =>
                                navigate(
                                    `/external-emergency-preparedness/bcp-plans/${planId}/recovery-tasks/${params.data._id}?mode=edit`
                                )
                            }
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Edit"
                        >
                            <Edit size={16} />
                        </button>
                        <button
                            onClick={() => handleUpdateProgress(params.data._id, params.data.progress)}
                            className="p-1 text-purple-600 hover:bg-purple-50 rounded text-xs font-semibold"
                            title="Update Progress"
                        >
                            %
                        </button>
                        <button
                            onClick={() => handleDelete(params.data._id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="px-2 py-4 mt-14 w-full">
            <div className="bg-white rounded-lg shadow-lg p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Recovery Tasks
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Manage recovery tasks for BCP Plan
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate(`/external-emergency-preparedness/bcp-plans/${planId}/recovery-timeline`)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                        >
                            View Timeline
                        </button>
                        <button
                            onClick={() => navigate(`/external-emergency-preparedness/bcp-plans/${planId}/add-recovery-task`)}
                            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center gap-2"
                        >
                            <Plus size={18} />
                            Add Task
                        </button>
                        <button
                            onClick={() => navigate("/external-emergency-preparedness/bcp-plans")}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                            Back to Plans
                        </button>
                    </div>
                </div>

                {/* Statistics */}
                {statistics && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Total Tasks</p>
                            <p className="text-2xl font-bold text-blue-600">{statistics.totalTasks}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Completed</p>
                            <p className="text-2xl font-bold text-green-600">{statistics.completedTasks}</p>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600">In Progress</p>
                            <p className="text-2xl font-bold text-yellow-600">{statistics.inProgressTasks}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Pending</p>
                            <p className="text-2xl font-bold text-gray-600">{statistics.pendingTasks}</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Avg Progress</p>
                            <p className="text-2xl font-bold text-purple-600">{statistics.averageProgress}%</p>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-2">
                        {["All", "Pending", "In Progress", "Completed", "Blocked"].map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium ${statusFilter === status
                                    ? "bg-teal-500 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {/* AG Grid Table */}
                <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                        }}
                        pagination={true}
                        paginationPageSize={10}
                        domLayout="normal"
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default RecoveryTasks;
